import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { MoneyDTO } from '../../../../../../../system/system-dtos';
import { ManualAdjustmentService } from '../manual-adjustment.service';
import { AdjustmentTypeCode, PlayerBalanceDTO } from '../manual-adjustment.model';
import { ManualAdjustmentInput, ManualAdjustmentMode } from './manual-adjustment-cash/manual-adjustment-cash.component';
import { MatStepper } from '@angular/material/stepper';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-app-manual-adjustment-modal',
  templateUrl: './app-manual-adjustment-modal.component.html',
  styleUrls: ['./app-manual-adjustment-modal.component.scss'],
  standalone: false,
})
export class AppManualAdjustmentModalComponent implements OnInit {
  types: AdjustmentType[] = [
    {
      name: 'Testing funds',
      description: 'funds for test accounts or specially authorised production accounts.',
      code: AdjustmentTypeCode.TESTING_FUNDS,
    },
    {
      name: 'Fraud',
      description: 'adjustments relating to any fraudulent activity, like chargebacks.',
      code: AdjustmentTypeCode.FRAUD,
    },
    {
      name: 'Charity',
      description: 'funds players have volunteered to be donated to charity when closing their accounts.',
      code: AdjustmentTypeCode.CHARITY,
    },
    {
      name: 'Discretionary',
      description:
        'authorised gestures of goodwill, within your own level of remit, intended to enhance the player experience or correct a poor one.',
      code: AdjustmentTypeCode.DISCRETIONARY,
    },
    {
      name: 'Manual offer',
      description:
        'offer adjustments that cannot be done automatically. Must be accompanied with an offer identifier so marketing can track this properly.',
      code: AdjustmentTypeCode.MANUAL_OFFER,
    },
    {
      name: 'Technical issue',
      description:
        'adjustments for technical issues that can ONLY be corrected manually. This must exactly match the technical discrepancy and not include any discretionary dispensation. A JIRA ID must be provided, where appropriate.',
      code: AdjustmentTypeCode.TECHNICAL_ISSUE,
    },
  ];

  formGroup: UntypedFormGroup;

  balance: PlayerBalanceDTO;

  cashAdjustmentInput: ManualAdjustmentInput;

  bonusAdjustmentInput: ManualAdjustmentInput;

  reserveAdjustmentInput: ManualAdjustmentInput;

  summary: ManualAdjustmentSummary;

  submitted = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalData,
    public dialogRef: MatDialogRef<AppManualAdjustmentModalComponent>,
    private fb: UntypedFormBuilder,
    private service: ManualAdjustmentService,
    private spinner: NgxSpinnerService
  ) {
    this.spinner.show();
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group(
      {
        type: [null, Validators.required],
        internalReason: ['', Validators.required],
        publicReason: ['', Validators.required],
        referenceIds: [''],
        cashSummary: [null, Validators.compose([nonZeroDelta(), nonNegative()])],
        diamondsSummary: [null, Validators.compose([nonZeroDelta(), nonNegative()])],
        bonusSummary: [null, Validators.compose([nonZeroDelta(), nonNegative()])],
        reserveSummary: [null, Validators.compose([nonZeroDelta(), nonNegative()])],
      },
      {
        validators: [atLeastOneDelta()],
      }
    );

    this.formGroup.get('type').valueChanges.subscribe((v) => {
      if (v === AdjustmentTypeCode.MANUAL_OFFER) {
        this.formGroup.get('referenceIds').setValidators(Validators.required);
      } else {
        this.formGroup.get('referenceIds').clearValidators();
      }
      this.formGroup.get('referenceIds').setValue(this.formGroup.get('referenceIds').value);
    });

    this.service.getBalancesForPlayer(this.data.playerId).subscribe((balance: PlayerBalanceDTO) => {
      this.cashAdjustmentInput = {
        mode: ManualAdjustmentMode.CASH,
        note: null,
        balance: balance.cash,
      };
      this.bonusAdjustmentInput = {
        mode: ManualAdjustmentMode.BONUS,
        note: balance.bonus ? `${balance.bonus.name} (#${balance.bonus.id})` : '',
        balance: balance.bonus?.balance,
      };
      this.reserveAdjustmentInput = {
        mode: ManualAdjustmentMode.RESERVE,
        note: null,
        balance: balance.reserve,
      };
      this.balance = balance;
      this.spinner.hide();
    });
  }

  copyMoney(money: MoneyDTO): MoneyDTO {
    return JSON.parse(JSON.stringify(money));
  }

  cashChanged(cashDelta: CashSummary): void {
    this.formGroup.get('cashSummary').setValue(cashDelta);
  }

  public reserveEnabled(): boolean {
    return this.formGroup.get('type').value === AdjustmentTypeCode.TECHNICAL_ISSUE;
  }

  reserveChanged(reserveDelta: CashSummary): void {
    this.formGroup.get('reserveSummary').setValue(reserveDelta);
  }

  diamondsChanged(diamondsDelta: DiamondsSummary) {
    this.formGroup.get('diamondsSummary').setValue(diamondsDelta);
  }

  public bonusEnabled(): boolean {
    return this.balance?.bonus !== undefined;
  }

  bonusChanged(bonusDelta: CashSummary): void {
    this.formGroup.get('bonusSummary').setValue(
      bonusDelta
        ? {
            before: bonusDelta.before,
            delta: bonusDelta.delta,
            after: bonusDelta.after,
            bonusId: this.balance.bonus.id,
            bonusName: this.balance.bonus.name,
          }
        : null
    );
  }

  makeChanges(): void {
    this.summary = undefined;
  }

  submit(): void {
    const formGroupValue = this.formGroup.value;

    this.summary = {
      type: formGroupValue.type,
      referenceIds: formGroupValue.referenceIds,
      internalReason: formGroupValue.internalReason,
      externalReason: formGroupValue.publicReason,
      cash: this.formGroup.get('cashSummary').value,
      reserve: this.formGroup.get('reserveSummary').value,
      diamonds: this.formGroup.get('diamondsSummary').value,
      bonus: this.formGroup.get('bonusSummary').value,
    };
  }

  authorise(stepper: MatStepper, submitBtn: MatButton): void {
    submitBtn.disabled = true;
    this.spinner.show();
    this.service
      .adjustForPlayer(this.data.playerId, {
        type: this.summary.type,
        internalReason: this.summary.internalReason,
        externalReason: this.summary.externalReason,
        references: this.summary.referenceIds,
        cashDelta: this.summary.cash?.delta,
        reserveDelta: this.summary.reserve?.delta,
        bonus: this.summary.bonus ? { id: this.balance.bonus.id, delta: this.summary.bonus.delta } : null,
        diamondsDelta: this.summary.diamonds?.delta,
      })
      .subscribe(
        () => {
          stepper.steps.toArray()[0].editable = false;
          stepper.steps.toArray()[1].editable = false;
          stepper.next();
          this.spinner.hide();
          submitBtn.disabled = false;
        },
        () => {
          this.spinner.hide();
          submitBtn.disabled = false;
        }
      );
  }
}

export function nonZeroDelta(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value?.delta === 0 || control.value?.delta?.value === 0) {
      return { zeroDelta: true };
    }
    return null;
  };
}

export function nonNegative(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value?.after < 0 || control.value?.after?.value < 0) {
      return { nonNegative: true };
    }
    return null;
  };
}

export function atLeastOneDelta(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      !control.value.cashSummary &&
      !control.value.diamondsSummary &&
      !control.value.bonusSummary &&
      !control.value.reserveSummary
    ) {
      return { noDelta: true };
    }
    return null;
  };
}

export interface AdjustmentType {
  name: string;
  description: string;
  code: AdjustmentTypeCode;
}

export interface ModalData {
  playerId: number;
}

export interface CashSummary {
  after: MoneyDTO;
  before: MoneyDTO;
  delta: MoneyDTO;
}

export interface BonusSummary {
  after: MoneyDTO;
  before: MoneyDTO;
  delta: MoneyDTO;
  bonusName: string;
  bonusId: number;
}

export interface DiamondsSummary {
  after: number;
  afterWorth: MoneyDTO;
  before: number;
  beforeWorth: MoneyDTO;
  delta: number;
  deltaWorth: MoneyDTO;
}

export interface ManualAdjustmentSummary {
  externalReason?: string;
  internalReason?: string;
  type?: AdjustmentTypeCode;
  bonus?: BonusSummary;
  diamonds?: DiamondsSummary;
  cash?: CashSummary;
  reserve?: CashSummary;
  referenceIds?: string;
}
